import { z } from 'zod';

/** A person's own defaults. A series can override each; the workspace sits underneath. */
export const userSettings = z.object({
  /** What your notetaker is called in the call. Null: the workspace's name, else "Recording for Your Name". */
  botDisplayName: z.string().nullable(),
  /** Record a meeting with exactly one other person. */
  recordOneToOnes: z.boolean(),
  /**
   * False: the notetaker joins only the meetings this person turned on — "Record
   * this one", or a series switched on (2026-10-08). True, the default, records
   * everything with a link.
   */
  recordEverything: z.boolean(),
  /** Transcription language for your meetings; 'auto' means no preference. */
  language: z.string(),
  /**
   * What language things are WRITTEN in — which is not the language they were
   * SPOKEN in (Vladimir, 2026-09-21: *"I want a setting on what language to
   * write meeting notes/commitment and knowledge"*). A Russian standup whose
   * notes are read by a English-reading team is the ordinary case, and until
   * now the note simply followed the transcript.
   *
   * `'auto'` everywhere means "the language the meeting was in", which is
   * what every note did before this existed. A series overrides the first
   * two; knowledge has no series to belong to, so it is set only here.
   */
  noteLanguage: z.string(),
  ledgerLanguage: z.string(),
  /**
   * What the product may write to you about (PRD 41). All four start on, and
   * every message carries a link that turns its own kind off without signing
   * in — his ruling, 2026-09-20: "On, with one click to stop".
   */
  mail: z.object({
    /** A meeting you were in has been written up. */
    noteReady: z.boolean(),
    /** Somebody gave you a meeting, a space or a folder. */
    shared: z.boolean(),
    /** Monday: what you owe and what you are owed. */
    weekly: z.boolean(),
    /** Half an hour before a meeting: what is open and who is coming. */
    beforeMeeting: z.boolean(),
  }),
  /**
   * The person's time zone, as their browser reports it (IANA, e.g.
   * "Europe/Berlin") — what "8:00 on a workday" means for them (PRD 59).
   * Null until they have opened the app since it was asked.
   */
  timeZone: z.string().nullable(),
  /** Your day, in Slack: every workday at `at`, their own time (PRD 59). On unless turned off. */
  dailyBrief: z.object({ on: z.boolean(), at: z.string() }),
  /**
   * The newest release whose notes this person has seen (2026-09-25), so
   * "what's new" shows only what they have not. Kept here rather than in the
   * browser, so a second device does not show it again. Null: never seen any.
   */
  releaseNotesSeen: z.string().nullable(),
});

/** The four things the product writes about. */
export const mailKind = z.enum(['noteReady', 'shared', 'weekly', 'beforeMeeting']);
export type MailKind = z.infer<typeof mailKind>;
export type UserSettings = z.infer<typeof userSettings>;

export const updateSettingsBody = z.object({
  botDisplayName: z.string().trim().min(1).max(80).nullable().optional(),
  recordOneToOnes: z.boolean().optional(),
  recordEverything: z.boolean().optional(),
  language: z.string().min(2).max(16).optional(),
  noteLanguage: z.string().min(2).max(16).optional(),
  ledgerLanguage: z.string().min(2).max(16).optional(),
  /** One switch at a time; the server merges rather than replacing the object. */
  mail: z
    .object({
      noteReady: z.boolean().optional(),
      shared: z.boolean().optional(),
      weekly: z.boolean().optional(),
      beforeMeeting: z.boolean().optional(),
    })
    .optional(),
  timeZone: z
    .string()
    .min(1)
    .max(64)
    .refine(isTimeZone, 'not a time zone this server knows')
    .optional(),
  dailyBrief: z
    .object({
      on: z.boolean().optional(),
      at: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .optional(),
    })
    .optional(),
  /** A version: major.minor.patch. */
  releaseNotesSeen: z
    .string()
    .regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/)
    .optional(),
});
export type UpdateSettingsBody = z.infer<typeof updateSettingsBody>;

/** Whether `zone` is an IANA time zone this runtime can compute with. */
export function isTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}
